import { z } from 'zod';
import { feedbackProseLimits, feedbackProseLength, isFeedbackProseWithinLimit } from '@/lib/feedbackAuthoringContinuation';
import type { CreateFeedbackDraftRequest, FeedbackDraftResponse, FeedbackWorkspaceResponse } from '@/lib/types';

export type FeedbackContent = CreateFeedbackDraftRequest['content'];
export type FeedbackSource = FeedbackDraftResponse | FeedbackWorkspaceResponse;
export type FeedbackPendingReference = Pick<FeedbackWorkspaceResponse,
  'pending_reference_input' | 'pending_reference_reason' | 'pending_reference_error'>;
export const emptyPendingReference: FeedbackPendingReference = {
  pending_reference_input: '', pending_reference_reason: '', pending_reference_error: '',
};
export const feedbackGoalIdSchema = z.union([z.number().int().positive().refine(Number.isSafeInteger),
  z.string().refine(value => /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n)]);
/** Comparison only. Captured commands retain the original number/string value. */
export function goalIdentity(value: number | string): string | null {
  return feedbackGoalIdSchema.safeParse(value).success ? String(value) : null;
}
const prose = (field: 'message' | 'next_step') => z.string().refine(value => isFeedbackProseWithinLimit(field, value),
  `Use ${feedbackProseLimits[field].toLocaleString()} characters or fewer.`);
const referenceText = (limit: number) => z.string().refine(value => [...value].length <= limit,
  `Use ${limit.toLocaleString()} characters or fewer.`).nullable().optional();

export const feedbackContentSchema = z.object({
  message: prose('message'), next_step: prose('next_step'),
  subject_mode: z.enum(['players', 'team']).nullable().optional(),
  player_ids: z.array(z.string()).max(50).optional(),
  audience: z.enum(['coaches', 'players', 'team']).nullable().optional(),
  associated_match_id: z.string().nullable().optional(),
  resource_links: z.array(z.object({ resource_id: z.string(), revision_id: z.string() })).max(10).optional(),
  goal_ids: z.array(feedbackGoalIdSchema).max(10).optional(),
  ref_video_provider: z.enum(['youtube', 'vimeo', 'file']).nullable().optional(),
  ref_video_id: referenceText(2048),
  ref_video_label: referenceText(200),
  ref_video_title: referenceText(300),
  ref_video_thumb: referenceText(500),
}).superRefine((value, context) => {
  const provider = !!value.ref_video_provider, id = !!value.ref_video_id?.trim();
  if (provider !== id || (!provider && [value.ref_video_label, value.ref_video_title, value.ref_video_thumb].some(Boolean)))
    context.addIssue({ code: 'custom', path: ['ref_video_id'], message: 'Check a video link or remove the external video before continuing.' });
});

export function emptyFeedbackContent(playerId?: string): FeedbackContent {
  return { message: '', next_step: '', subject_mode: playerId ? 'players' : null,
    player_ids: playerId ? [playerId] : [], audience: playerId ? 'players' : null,
    associated_match_id: null, resource_links: [], goal_ids: [], ref_video_provider: null,
    ref_video_id: null, ref_video_label: null, ref_video_title: null, ref_video_thumb: null };
}

/** Presentation only; never persist a second authored title. */
export function feedbackMessageExcerpt(message: string, draft = true): string {
  const line = message.split(/\r?\n/).map(part => part.trim()).find(Boolean);
  if (!line) return draft ? 'Feedback draft' : 'Feedback';
  const points = [...line];
  return points.length > 80 ? points.slice(0, 79).join('') + '…' : line;
}

/** Q04 replaces the complete envelope. Tombstones require the Q03 repair seam;
 * filtering them here would silently remove relationships from saved Feedback. */
export function editableDraftContent(draft: FeedbackSource): FeedbackContent | null {
  if (draft.match_context_erased || draft.subjects.some(subject => subject.erased || !subject.player_id)
    || ('entry' in draft && draft.entry.erased)
    || draft.goals.some(goal => goal.erased || goal.goal_id === null || goalIdentity(goal.goal_id) === null || goal.player_id === null || goal.descriptor === null
      || !!goal.descriptor && (goal.descriptor.goal_id !== goalIdentity(goal.goal_id) || goal.descriptor.player_id !== goal.player_id))
    || draft.resource_links.some(pin => pin.resource.availability !== 'available')) return null;
  return { message: draft.message, next_step: draft.next_step, subject_mode: draft.subject_mode,
    player_ids: draft.subjects.map(subject => subject.player_id!), audience: draft.audience,
    associated_match_id: draft.associated_match_id,
    resource_links: draft.resource_links.map(({ resource_id, revision_id }) => ({ resource_id, revision_id })),
    goal_ids: draft.goals.map(goal => goal.goal_id!),
    ref_video_provider: draft.external_reference?.ref_video_provider ?? null,
    ref_video_id: draft.external_reference?.ref_video_id ?? null,
    ref_video_label: draft.external_reference?.ref_video_label ?? null,
    ref_video_title: draft.external_reference?.ref_video_title ?? null,
    ref_video_thumb: draft.external_reference?.ref_video_thumb ?? null };
}

export function publicationProblem(content: FeedbackContent): string | null {
  if (feedbackProseLength(content.message) === 0) return 'Write a message before publishing.';
  if (!content.subject_mode || (content.subject_mode === 'players' && !content.player_ids?.length))
    return 'Choose who this Feedback is for.';
  if (!content.audience) return 'Choose who to share this Feedback with.';
  if (content.subject_mode === 'team' && content.audience === 'players'
    || content.subject_mode === 'players' && content.audience === 'team') return 'Choose sharing for the selected subjects.';
  return null;
}
